/* eslint-disable newline-per-chained-call */
import {
  COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT,
  MAX_VALUE_IS,
  MIN_VALUE_IS,
  MIN_VALUE_IS_ONE,
  NUMBERS_ONLY,
  NUMBER_CANNOT_BE_NEGATIVE,
  REQUIRED_FIELD,
  MUST_BE_INTEGER,
  FLEX_AMOUNT_MORE_THAN_ADVANCED_AMOUNT,
  FLEX_AMOUNT_LESS_THAN_ADVANCED_AMOUNT,
  INCORRECT_COMMISSION_SPLIT_TOTAL,
  INVALID_PRICE_FORMAT,
  DEAL_START_DATE_REQUIRED,
  DEAL_START_DATE_IN_PAST,
  INVALID_DATE_MESSAGE,
  DEAL_FIRST_PAYMENT_DATE_REQUIRED,
  FIRST_PAYMENT_DATE_EARLIER,
  COMMISSION_DUE_DATE_IN_PAST,
  COMMISSION_DATE_EARLIER,
  DISBURSEMENT_DATE_IN_PAST,
  DISBURSEMENT_DATE_EARLIER,
  PAYMENT_DAY_REQUIRED
} from 'constants/errors'
import { paymentDays } from 'constants/paymentDay'
import { paymentFrequency } from 'constants/paymentFrequency'
import * as yup from 'yup'
import { integerReg, priceReg } from 'constants/regExp'
import { IDocuments, IExtendType, IFlexDisbursement, IUploadedDocuments, extendTypes } from 'typescript/interfaces/deals'
import { IBankAccountClient } from 'typescript/interfaces/clients'
import { getBankStatementFileName, IMonthWindowEntry } from 'utils/bankStatementHelpers'
import isValidDate from 'utils/isValidDate'
import { isBeforeDate, isDateInPast, isSameCalendarDate } from 'utils/dateUtils'
import Decimal from 'decimal.js'
import { mathRoundingAmount } from 'utils/rounding'

export const schemaInHouseDeal = yup.object().shape({
  clientId: yup.number().typeError(NUMBERS_ONLY).required(REQUIRED_FIELD),
  companyId: yup.string().required(REQUIRED_FIELD),
  isoId: yup.number().typeError(NUMBERS_ONLY),
  paymentFrequency: yup.string().oneOf(paymentFrequency).required(REQUIRED_FIELD),
  paymentDay: yup.mixed().when('paymentFrequency', {
    is: 'Weekly',
    then: yup.string().oneOf(paymentDays, PAYMENT_DAY_REQUIRED).required(PAYMENT_DAY_REQUIRED),
    otherwise: yup.string().nullable(),
  }),
  disbursementFrequency: yup.mixed().when('extendType', {
    is: <IExtendType>'Reverse',
    then: yup.string().oneOf(paymentFrequency).required(REQUIRED_FIELD),
    otherwise: yup.string().nullable(),
  }),
  startDate: yup
    .string()
    .nullable()
    .required(DEAL_START_DATE_REQUIRED)
    .test('startDateFormat', INVALID_DATE_MESSAGE, val => isValidDate(val))
    .test('startDateNotPast', DEAL_START_DATE_IN_PAST, function (val) {
      return isSameCalendarDate(val, this.parent.initialStartDate) || !isDateInPast(val)
    }),
  firstPaymentDate: yup
    .string()
    .nullable()
    .required(DEAL_FIRST_PAYMENT_DATE_REQUIRED)
    .test('firstPaymentDateFormat', INVALID_DATE_MESSAGE, val => isValidDate(val))
    .test('firstPaymentDateEarlierStartDate', FIRST_PAYMENT_DATE_EARLIER, function (value) {
      const { startDate } = this.parent      
      if (!value || !startDate) return true
      return isSameCalendarDate(value, this.parent.initialFirstPaymentDate) || !isBeforeDate(value, startDate)
      }
    ),
  advanceAmount: yup.string().required(REQUIRED_FIELD).typeError(NUMBERS_ONLY).matches(priceReg, INVALID_PRICE_FORMAT)
    .test('minPayoffBalance', 'Advance amount must be higher than the total prior balance', function (val) {
      const externalDeals = this.parent.externalDeals || []
      const externalPayoff = externalDeals.reduce(
        (sum: number, ed: { payoffBalance?: number }) => sum + (Number(ed.payoffBalance) || 0), 0
      )
      const parentRemaining = this.parent.isRefiDeal ? Number(this.parent.payOffAmount) || 0 : 0
      const totalPayoff = externalPayoff + parentRemaining
      if (totalPayoff === 0) return true
      return Number(val) > totalPayoff
    }),
  commissionToRep: yup
    .string()
    .nullable()
    .when('representativeIds', {
      is: val => !!val,
      then: yup.string().required(REQUIRED_FIELD),
    })
    .test('comission', COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, function (value) {
      if (!this.parent.representativeIds) return true

      const rFee = value || 0
      const amount = this.parent.advanceAmount || 0

      return amount * 0.15 >= Number(rFee)
    })
    .matches(priceReg, INVALID_PRICE_FORMAT)
    .typeError(NUMBERS_ONLY)
    .min(0, NUMBER_CANNOT_BE_NEGATIVE),
  isoFee: yup
    .string()
    .test('comission', COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, function () {
      if (!this.parent.isoIds?.length) return true

      const iFee = this.parent.isoFee || 0
      const amount = this.parent.advanceAmount || 0

      return amount * 0.15 >= iFee
    })
    .matches(priceReg, INVALID_PRICE_FORMAT)
    .typeError(NUMBERS_ONLY)
    .min(0, NUMBER_CANNOT_BE_NEGATIVE),
  isoSplitCommission: yup.array().test('isoSplitCommission', INCORRECT_COMMISSION_SPLIT_TOTAL, function (value) {
    if (!value?.length) return true
    const isoFee = new Decimal(this.parent.isoFee || 0)
    if (isoFee.isZero()) return true
    return (value as Array<{ value: number }>)
      .map(x => new Decimal(x.value))
      .reduce((a, b) => a.plus(b), new Decimal(0))
      .equals(isoFee)
  }),
  commissionSplit: yup.number().typeError(NUMBERS_ONLY).min(0, NUMBER_CANNOT_BE_NEGATIVE),
  psfFee: yup
    .string()
    .test('comission', COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, function () {
      const pFee = this.parent.psfFee || 0
      const amount = this.parent.advanceAmount || 0

      return amount * 0.15 >= pFee
    })
    .matches(priceReg, INVALID_PRICE_FORMAT)
    .typeError(NUMBERS_ONLY)
    .min(0, NUMBER_CANNOT_BE_NEGATIVE),
  isoCommissionDueDate: yup
    .string()
    .nullable()
    .test('isoCommissionDueDateFormat', INVALID_DATE_MESSAGE, function (val) {
      if (!val) return true;
      return isValidDate(val)
    })
    .test('isoCommissionDueDateEarlier', COMMISSION_DATE_EARLIER, function (val) {
      if (!val) return true;
      return !isBeforeDate(val, this.parent.startDate)
    })
    .test('isoCommissionDueDateInPast', COMMISSION_DUE_DATE_IN_PAST, function (val) {
      if (!val) return true;
      return isSameCalendarDate(val, this.parent.initialIsoCommissionDueDate) || !isDateInPast(val)
    }),
  repCommissionDueDate: yup
    .string()
    .nullable()
    .test('repCommissionDueDateFormat', INVALID_DATE_MESSAGE, function (val) {
      if (!val) return true;
      return isValidDate(val)
    })
    .test('repCommissionDueDateEarlier', COMMISSION_DATE_EARLIER, function (val) {
      if (!val) return true;
      return !isBeforeDate(val, this.parent.startDate)
    })
    .test('repCommissionDueDateInPast', COMMISSION_DUE_DATE_IN_PAST, function (val) {
      if (!val) return true;
      return isSameCalendarDate(val, this.parent.initialRepCommissionDueDate) || !isDateInPast(val)
    }),
  syndicationFee: yup.string().matches(priceReg, INVALID_PRICE_FORMAT).typeError(NUMBERS_ONLY).min(0, NUMBER_CANNOT_BE_NEGATIVE),
  payOffAmount: yup.string().matches(priceReg, INVALID_PRICE_FORMAT).typeError(NUMBERS_ONLY).min(0, NUMBER_CANNOT_BE_NEGATIVE),
  comments: yup.string(),
  term: yup
    .string()
    .when('paymentFrequency', {
      is: 'Customised',
      then: yup.string().nullable(),
      otherwise: yup.string().required(REQUIRED_FIELD).matches(integerReg, MUST_BE_INTEGER).test('integer', MIN_VALUE_IS_ONE, function (val) {
        return parseInt(val || '') >= 1
      }),
    }),
  reverseTerm: yup.mixed().when('extendType', {
    is: <IExtendType>'Reverse',
    then: yup
      .string()
      .required(REQUIRED_FIELD)
      .matches(integerReg, MUST_BE_INTEGER)
      .test('integer', MIN_VALUE_IS_ONE, function (val) {
        return parseInt(val || '') >= 1
      }),
    otherwise: yup.string().nullable(),
  }),
  factor: yup
    .number()
    .typeError(NUMBERS_ONLY)
    .min(1, MIN_VALUE_IS.replace(':value', '1'))
    .max(2, MAX_VALUE_IS.replace(':value', '2'))

    .required(REQUIRED_FIELD),
  representativeIds: yup.array().of(yup.number()).nullable(),
  bankAccountId: yup.string().required(REQUIRED_FIELD),
  flexAmount: yup.mixed().when('isFlexDeal', {
    is: true,
    then: yup
      .string()
      .required(REQUIRED_FIELD)
      .typeError(NUMBERS_ONLY)
      .matches(priceReg, INVALID_PRICE_FORMAT)
      .test('flexAmount', FLEX_AMOUNT_MORE_THAN_ADVANCED_AMOUNT, function (val) {
        const totalDisbursement = (this.parent.flexSchedule as IFlexDisbursement[] | undefined)
          ?.map(({ amount }) => Number(amount) || 0)
          ?.reduce((a, b) => a + b, 0)
        const totalFlex = (Number(val) || 0) + (totalDisbursement || 0)
        const advanceAmount = Number(this.parent.advanceAmount) || 0
        // Round to 2 decimal places to avoid floating point precision issues
        const roundedTotalFlex = mathRoundingAmount(totalFlex)
        const roundedAdvanceAmount = mathRoundingAmount(advanceAmount)
        return roundedTotalFlex <= roundedAdvanceAmount
      })
      .test('flexAmountMinimum', FLEX_AMOUNT_LESS_THAN_ADVANCED_AMOUNT, function (val) {
        const totalDisbursement = (this.parent.flexSchedule as IFlexDisbursement[] | undefined)
          ?.map(({ amount }) => Number(amount) || 0)
          ?.reduce((a, b) => a + b, 0)
        const totalFlex = (Number(val) || 0) + (totalDisbursement || 0)
        const advanceAmount = Number(this.parent.advanceAmount) || 0
        // Round to 2 decimal places to avoid floating point precision issues
        const roundedTotalFlex = mathRoundingAmount(totalFlex)
        const roundedAdvanceAmount = mathRoundingAmount(advanceAmount)
        return roundedTotalFlex >= roundedAdvanceAmount
      }),
    otherwise: yup.mixed(),
  }),
  flexSchedule: yup.array().of(
    yup.object().shape({
      date: yup
        .string()
        .nullable()
        .required(REQUIRED_FIELD)
        .test('date', INVALID_DATE_MESSAGE, val => isValidDate(val))
        .test('dateNotPast', DISBURSEMENT_DATE_IN_PAST, function (val) {
          const rootValues = (this.options as any).from?.slice(-1)[0]?.value
          const persistedDate = (rootValues?.initialFlexSchedule as IFlexDisbursement[] | undefined)
            ?.find(disbursement => disbursement.id === (this.parent as IFlexDisbursement).id)?.date

          return isSameCalendarDate(val, persistedDate) || !isDateInPast(val)
        })
        .test('dateNotEarlierDealStartDate', DISBURSEMENT_DATE_EARLIER, function (val) {
          const rootValues = (this.options as any).from?.slice(-1)[0]?.value
          const { startDate } = rootValues

          return isSameCalendarDate(val, startDate) || !isBeforeDate(val, startDate)
        }),
    })
  ),
  extendType: yup.mixed().when('isFlexDeal', {
    is: true,
    then: yup.string().oneOf(extendTypes).required(REQUIRED_FIELD),
    otherwise: yup.string().nullable(),
  }),
  monthlyRevenue: yup.number().typeError(NUMBERS_ONLY).required(REQUIRED_FIELD).min(0, NUMBER_CANNOT_BE_NEGATIVE),
  specifiedPercentage: yup
    .number()
    .typeError(NUMBERS_ONLY)
    .required(REQUIRED_FIELD)
    .min(0, NUMBER_CANNOT_BE_NEGATIVE)
    .max(100, MAX_VALUE_IS.replace(':value', '100')),
  clientState: yup.string(),
  apr: yup
    .number()
    .typeError(NUMBERS_ONLY)
    .min(0, NUMBER_CANNOT_BE_NEGATIVE)
    .when('clientState', (state, schema) => (['NY', 'CA'].includes(state) ? schema.required(REQUIRED_FIELD) : schema)),
  customPaymentSchedule: yup.object().when('paymentFrequency', {
    is: 'Customised',
    then: yup.object().shape({
      frequency: yup.string().oneOf(['Daily', 'Weekly', 'Biweekly']).required(REQUIRED_FIELD),
      payments: yup.array().min(1, 'At least one payment is required').required(REQUIRED_FIELD)
    }),
    otherwise: yup.object().nullable(),
    })
})

interface IDocumentsValidationSchemaParams {
  bankAccounts?: IBankAccountClient[]
  monthWindow: IMonthWindowEntry[]
  referenceDate: Date
  clientState?: string
  isRefiDeal: boolean
  bankStatementsRequired: boolean
}

export const getDocumentsValidationSchema = ({
  bankAccounts,
  monthWindow,
  referenceDate,
  clientState,
  isRefiDeal,
  bankStatementsRequired,
}: IDocumentsValidationSchemaParams) => {
  return yup.object().shape({
    documents: yup.object().shape({
      VoidedCheck: yup.array().test('notEmpty', REQUIRED_FIELD, function () {
        const opts = this.options as any
        const { value: grandparent } = opts.from[1]
        return this.parent?.VoidedCheck.length > 0 || grandparent?.uploadedDocuments.VoidedCheck.length > 0
      }),
      Application: yup.array().test('notEmpty', REQUIRED_FIELD, function () {
        const opts = this.options as any
        const { value: grandparent } = opts.from[1]
        return this.parent.Application.length > 0 || grandparent?.uploadedDocuments?.Application.length > 0
      }),
      DriverLicense: yup.array().test('notEmpty', REQUIRED_FIELD, function () {
        const opts = this.options as any
        const { value: grandparent } = opts.from[1]
        return this.parent.DriverLicense.length > 0 || grandparent?.uploadedDocuments?.DriverLicense.length > 0
      }),
      FundingCallRecord: yup.array().test('is-audio-or-video', 'Only audio or video files are allowed', files => {
        if (!files || files.length === 0) return true
        return files.every((file: any) => file && (file.type?.startsWith('audio/') || file.type?.startsWith('video/')))
      }),
      BankStatements: yup.array().test('required', REQUIRED_FIELD, function () {
        if (!bankAccounts || !monthWindow?.length || !clientState || isRefiDeal || !bankStatementsRequired) return true

        const requiredMonths = getRequiredMonths(4, monthWindow, referenceDate)
        const requiredNames = bankAccounts.flatMap(({ bankAccount }) =>
          requiredMonths.map(({ month, year }) => getBankStatementFileName(bankAccount, month, year))
        )

        const files = <IDocuments>this.parent
        const uploadedFiles: IUploadedDocuments = (<any>this.options).from[1]?.value?.uploadedDocuments
        const existingNames = [...files.BankStatements.map(x => x.name), ...uploadedFiles.BankStatements.map(x => x.description)]

        const errors = requiredNames
          .filter(x => !existingNames.some(e => e.startsWith(x)))
          .map(x => `${x} is required`)
          .join('; ')

        return errors ? this.createError({ message: errors }) : true
      }),
    }),
    uploadedDocuments: yup.object().shape({}),
  })
}

export const merchantClientsValidationSchema = yup.object({
  clients: yup.array().of(
    yup.object().shape({
      amountOwing: yup.number().typeError(NUMBERS_ONLY).min(0, NUMBER_CANNOT_BE_NEGATIVE),
    })
  ),
})

const getRequiredMonths = (quantity: number, monthWindow: IMonthWindowEntry[], referenceDate: Date): IMonthWindowEntry[] => {
  const refMonth = referenceDate.getMonth()
  const refYear = referenceDate.getFullYear()
  const referenceMonth8th = new Date(refYear, refMonth, 8)

  const currentIndex = monthWindow.findIndex(entry => entry.monthIndex === refMonth && entry.year === refYear)
  if (currentIndex === -1) return []

  const lastIndex = referenceDate >= referenceMonth8th ? currentIndex : currentIndex - 1
  const startIndex = Math.max(lastIndex - quantity + 1, 0)

  return monthWindow.slice(startIndex, lastIndex + 1)
}
